import { MISSION_TYPES, PROJECT_STATUSES } from '@batiflow/contracts';
import { z } from 'zod';

/**
 * Tabs of a project (12-frontend.md §2): the open tab is a segment of the address. `finance` is
 * permission-gated (shown to members holding `contract.read`, `invoice.read` or `quote.read`);
 * `profitability` too (members holding `project.fees.read`).
 */
export const PROJECT_TABS = [
  'posts',
  'files',
  'tasks',
  'phases',
  'planning',
  'deliverables',
  'approvals',
  'register',
  'checklists',
  'observations',
  'meetings',
  'calendar',
  'finance',
  'profitability',
  'settings',
] as const;
export type ProjectTab = (typeof PROJECT_TABS)[number];

export const DEFAULT_PROJECT_TAB: ProjectTab = 'posts';

export const isProjectTab = (value: string): value is ProjectTab =>
  (PROJECT_TABS as readonly string[]).includes(value);

/**
 * Sections of the project workspace (the project full screen, in its own browser tab): an
 * overview first, then every tab of the project page, in groups.
 */
export const WORKSPACE_SECTIONS = ['overview', ...PROJECT_TABS] as const;
export type WorkspaceSection = (typeof WORKSPACE_SECTIONS)[number];

export const DEFAULT_WORKSPACE_SECTION: WorkspaceSection = 'overview';

export const isWorkspaceSection = (value: string): value is WorkspaceSection =>
  (WORKSPACE_SECTIONS as readonly string[]).includes(value);

/** The groups of the workspace's navigation, in order. */
export const WORKSPACE_GROUPS = [
  { id: 'steer', sections: ['overview', 'tasks', 'phases', 'planning', 'deliverables'] },
  { id: 'documents', sections: ['files', 'approvals', 'register', 'checklists', 'observations'] },
  { id: 'collaborate', sections: ['posts', 'meetings', 'calendar'] },
  { id: 'manage', sections: ['finance', 'profitability', 'settings'] },
] as const satisfies readonly { id: string; sections: readonly WorkspaceSection[] }[];

export const PROJECT_VIEWS = ['grid', 'list'] as const;
export type ProjectView = (typeof PROJECT_VIEWS)[number];

/** A text the router may have parsed as a number ("2026" → 2026): back to text. */
const text = (max: number) =>
  z
    .preprocess(
      (value) =>
        typeof value === 'string' || typeof value === 'number' ? String(value) : undefined,
      z.string().trim().min(1).max(max).optional(),
    )
    .catch(undefined);

/**
 * Search params of /o/$orgId/projects: the filters and the grid / list choice, kept in the address
 * so a filtered view can be shared and survives a reload. An invalid value is simply dropped.
 */
export const ProjectsSearchSchema = z.object({
  q: text(100),
  status: z.enum(PROJECT_STATUSES).optional().catch(undefined),
  missionType: z.enum(MISSION_TYPES).optional().catch(undefined),
  clientId: text(64),
  mine: z.literal(true).optional().catch(undefined),
  view: z.enum(PROJECT_VIEWS).optional().catch(undefined),
});
export type ProjectsSearch = z.infer<typeof ProjectsSearchSchema>;
